import {
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import {
  AppDialogRef,
  AppDialogService,
} from '@nimmsta/ng-commons-lib/services/app-dialog';
import {
  Column,
  CustomAction,
  CustomTableAction,
  DataTableComponent,
  NimmstaDataSource,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  DataConnection,
  DataConnectionType,
} from '@nimmsta/ng-commons-lib/models';
import { NimmstaTimeAgoPipe } from '@nimmsta/ng-commons-lib/pipes';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { DataConnectionFormComponent } from './dataconnection-form.component';
import { CsvComponent } from './types/csv/csv.component';
import { SortingUploadComponent } from './types/sorting/sorting-upload.component';
import { ExecuteDataConnectionAction } from './executeDataConnectionAction';
import { DataConnectionService } from '../services/dataConnection.service';
import { DataConnectionTemplateService } from '../services/dataConnectionTemplate.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { SelectModule } from 'primeng/select';

_('dataconnection.title');

@Component({
  selector: 'app-dataconnection',
  templateUrl: './dataconnection.component.html',
  imports: [
    DataTableComponent,
    EmptyTableComponent,
    HeaderBarComponent,
    ErrorComponent,
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    TranslatePipe,
    SelectModule,
  ],
  providers: [NimmstaTimeAgoPipe],
})
export class DataConnectionComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private service = inject(DataConnectionService);
  private templateService = inject(DataConnectionTemplateService);
  private modalService = inject(AppDialogService);
  private timeago = inject(NimmstaTimeAgoPipe);

  source!: NimmstaDataSource<DataConnection>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<DataConnection>;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  @ViewChild('selectTemplateModal', { static: true })
  selectTemplateModal!: TemplateRef<unknown>;

  templates?: DataConnection[];

  selectedTemplate?: DataConnection;

  modalRef?: AppDialogRef;

  customActions: CustomAction<DataConnection>[] = [];

  customTableActions: CustomTableAction<DataConnection>[] = [];

  error?: HttpErrorResponse | undefined;

  canWriteDataConnections = false;

  canUploadJobs = false;

  get columns(): Column[] {
    return [
      new Column('name', 'dataconnection.name', { sortable: true }),
      new Column('description', 'dataconnection.description'),
      new Column('dataConnectionHandler', 'dataconnection.type', {
        sortable: true,
        translate: true,
      }),
      new Column('lastUpdatedAt', 'lastUpdatedAt', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.canWriteDataConnections = this.authorizationService.hasPermission(
      'data_connection.write',
    );
    this.canUploadJobs = this.authorizationService.hasPermission('job.upload');
    this.source = new NimmstaDataSource<DataConnection>(this.service);

    if (this.canWriteDataConnections) {
      const createFromTemplateAction = new CustomTableAction(
        'dataconnection.template.create-from',
        'add_box',
      );
      this.customTableActions.push(createFromTemplateAction);
      createFromTemplateAction.subscribe(this.createFromTemplate.bind(this));

      const editAction = new CustomAction('edit');
      this.customActions.push(editAction);
      editAction.subscribe(this.edit.bind(this));

      const deleteAction = new CustomAction('delete', false, true);
      this.customActions.push(deleteAction);
      deleteAction.subscribe(this.delete.bind(this));

      const importAction = new CustomTableAction('import', 'download_2');
      this.customTableActions.push(importAction);
      importAction.subscribe(this.import.bind(this));
    }

    if (this.canUploadJobs) {
      const executeAction = new ExecuteDataConnectionAction(
        'dataconnection.execute',
        true,
      );
      this.customActions.push(executeAction);
      executeAction.subscribe(this.execute.bind(this));
    }

    const exportAction = new CustomAction('export');
    this.customActions.push(exportAction);
    exportAction.subscribe(this.export.bind(this));
  }

  new(): void {
    this.openModal({} as DataConnection);
  }

  show(id: string): void {
    this.edit(id);
  }

  edit(id: string): void {
    this.openModal(this.getSelectedObject(id)!);
  }

  execute(id: string): void {
    const dataConnection = this.getSelectedObject(id)!;
    switch (dataConnection.dataConnectionHandler) {
      case DataConnectionType.SortingDataConnection:
        this.modalService.open(SortingUploadComponent, {
          data: { dataConnection },
          closeOnEscape: false,
          dismissableMask: false,
          closable: false,
        });
        break;
      case DataConnectionType.CSVDataConnection:
        this.modalService.open(CsvComponent, {
          data: {
            dataConnection,
          },
        });
        break;
      default:
        this.service.execute(dataConnection).subscribe({
          error: (error: HttpErrorResponse) => {
            this.error = error;
          },
        });
        break;
    }
  }

  openModal(value: DataConnection): void {
    const ref = this.modalService.open(DataConnectionFormComponent, {
      data: {
        value,
      },
      size: 'xl',
    });
    const sub = ref.closed.subscribe(() => {
      this.tableComponent.loadDataPage();
      sub.unsubscribe();
    });
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  getSelectedObject(id: string): DataConnection | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }

  export(id: string): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clone = { ...this.getSelectedObject(id)! } as any;
    clone.configuration = { ...clone.configuration };
    const identifier = clone.identifier;
    delete clone.id;
    delete clone.name;
    delete clone.identifier;
    delete clone.configuration.password;
    delete clone.configuration.token;
    const element = document.createElement('a');
    const fileType = 'text/json';
    const code = JSON.stringify(clone, null, 2);
    element.setAttribute(
      'href',
      `data:${fileType};charset=utf-8,${encodeURIComponent(code)}`,
    );
    element.setAttribute('download', `${identifier}.json`);
    const event = new MouseEvent('click');
    element.dispatchEvent(event);
    element.remove();
  }

  import(): void {
    this.fileInput?.nativeElement.click();
  }

  importFile(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.openModal(JSON.parse(reader.result as string));
      };
      reader.readAsText(file);
    }
    target.value = '';
  }

  public createFromTemplate(): void {
    this.modalRef = this.modalService.open(this.selectTemplateModal);
    this.templateService
      .list({
        filter: ['status:ACTIVE-or'],
      })
      .subscribe((response) => {
        this.templates = response.content;
      });
  }

  public selectTemplate(): void {
    this.modalRef?.close();
    this.modalRef = undefined;
    // Slight delay to prevent modal from closing before the new modal is opened (Looks smoother)
    setTimeout(() => {
      const newConnection = JSON.parse(JSON.stringify(this.selectedTemplate));
      delete newConnection.id;
      this.openModal(JSON.parse(JSON.stringify(newConnection)));
    }, 300);
  }
}
